import { FormEvent, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Clock3, Eye, FileJson2, Filter, History, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminApiError,
  AdminStoreActivityLog,
  AdminStoreActivityLogOption,
  AdminStoreActivityLogUser,
  AdminStoreUserLocation,
  AdminUser,
  PaginationMeta,
  getCurrentAdmin,
  listAdminStoreActivityLogs,
  logout,
} from "./api";
import Pagination from "./components/Pagination";

const defaultPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };
const currentYear = new Date().getFullYear();
const defaultDateFrom = `${currentYear}-01-01`;
const defaultDateTo = `${currentYear}-12-31`;

const fallbackEntityTypes: AdminStoreActivityLogOption[] = [
  { Key: "StoreSalesOrder", Label: "門市訂單" },
  { Key: "HearingServiceRecord", Label: "服務紀錄" },
  { Key: "CustomerInteraction", Label: "回訪/互動" },
  { Key: "Customer", Label: "客戶資料" },
  { Key: "Reservation", Label: "預約" },
];

const fallbackActions: AdminStoreActivityLogOption[] = [
  { Key: "Create", Label: "新增" },
  { Key: "Update", Label: "修改" },
  { Key: "Void", Label: "作廢" },
  { Key: "Complete", Label: "完成" },
  { Key: "Postpone", Label: "延期" },
  { Key: "AddInteraction", Label: "新增互動" },
  { Key: "StatusUpdate", Label: "狀態更新" },
  { Key: "AddNote", Label: "新增備註" },
];

const actionTone: Record<string, string> = {
  Create: "bg-[#e9f4ec] text-[#245438]",
  Update: "bg-[#eef3f8] text-[#255071]",
  Void: "bg-[#fdebea] text-[#9a2d2d]",
  Complete: "bg-[#e9f4ec] text-[#245438]",
  Postpone: "bg-[#fff7e8] text-[#9a5b16]",
  StatusUpdate: "bg-[#f1f8f3] text-[#245438]",
  AddInteraction: "bg-[#eef3f8] text-[#255071]",
  AddNote: "bg-[#f4f6f5] text-[#59665f]",
};

export default function AdminStoreActivityLogs() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [logs, setLogs] = useState<AdminStoreActivityLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<AdminStoreActivityLog | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [locations, setLocations] = useState<AdminStoreUserLocation[]>([]);
  const [storeUsers, setStoreUsers] = useState<AdminStoreActivityLogUser[]>([]);
  const [entityTypeOptions, setEntityTypeOptions] = useState<AdminStoreActivityLogOption[]>(fallbackEntityTypes);
  const [actionOptions, setActionOptions] = useState<AdminStoreActivityLogOption[]>(fallbackActions);
  const [keyword, setKeyword] = useState("");
  const [filterLocationIds, setFilterLocationIds] = useState<string[]>([]);
  const [storeUserId, setStoreUserId] = useState("");
  const [entityType, setEntityType] = useState("");
  const [action, setAction] = useState("");
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");

  const storeUserLabelMap = useMemo(() => Object.fromEntries(storeUsers.map((item) => [item.StoreUserId, item.DisplayName])), [storeUsers]);
  const entityTypeLabelMap = useMemo(() => Object.fromEntries(entityTypeOptions.map((item) => [item.Key, item.Label])), [entityTypeOptions]);
  const actionLabelMap = useMemo(() => Object.fromEntries(actionOptions.map((item) => [item.Key, item.Label])), [actionOptions]);
  const locationLabelMap = useMemo(() => Object.fromEntries(locations.map((item) => [item.LocationId, item.Name])), [locations]);
  const filterSummary = useMemo(() => {
    const parts = [];
    if (keyword.trim()) parts.push(`關鍵字：${keyword.trim()}`);
    if (storeUserId) parts.push(`人員：${storeUserLabelMap[storeUserId] || storeUserId}`);
    if (entityType) parts.push(`資料：${entityTypeLabelMap[entityType] || entityType}`);
    if (action) parts.push(`動作：${actionLabelMap[action] || action}`);
    if (dateFrom !== defaultDateFrom || dateTo !== defaultDateTo) parts.push(`日期：${dateFrom || "-"} ~ ${dateTo || "-"}`);
    if (filterLocationIds.length > 0) {
      const locationText = filterLocationIds.slice(0, 2).map((id) => locationLabelMap[id] || id).join("、");
      parts.push(`門市：${locationText}${filterLocationIds.length > 2 ? ` 等 ${filterLocationIds.length} 間` : ""}`);
    }
    return parts.length ? parts.join(" / ") : "預設：今年全部操作紀錄";
  }, [action, actionLabelMap, dateFrom, dateTo, entityType, entityTypeLabelMap, filterLocationIds, keyword, locationLabelMap, storeUserId, storeUserLabelMap]);

  const selectedSummary = useMemo(() => {
    if (!selectedLog) return "";
    return `${selectedLog.EntityTypeLabel} #${selectedLog.EntityId} / ${selectedLog.ActionLabel}`;
  }, [selectedLog]);

  async function load(filters = { Keyword: keyword, LocationIds: filterLocationIds, StoreUserId: storeUserId, EntityType: entityType, Action: action, DateFrom: dateFrom, DateTo: dateTo, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminStoreActivityLogs(filters);
      setLogs(data.Logs || []);
      setCounts(data.Counts || {});
      setPagination(data.Pagination || defaultPagination);
      setLocations(data.Locations || []);
      setStoreUsers(data.StoreUsers || []);
      setEntityTypeOptions(data.EntityTypeOptions?.length ? data.EntityTypeOptions : fallbackEntityTypes);
      setActionOptions(data.ActionOptions?.length ? data.ActionOptions : fallbackActions);
      setSelectedLog((current) => {
        if (!current) return data.Logs?.[0] || null;
        return data.Logs?.find((item) => item.ActivityLogId === current.ActivityLogId) || data.Logs?.[0] || null;
      });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "門市操作紀錄載入失敗。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Keyword: "", LocationIds: [], StoreUserId: "", EntityType: "", Action: "", DateFrom: defaultDateFrom, DateTo: defaultDateTo, Page: 1, PerPage: defaultPagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Keyword: keyword.trim(), LocationIds: filterLocationIds, StoreUserId: storeUserId, EntityType: entityType, Action: action, DateFrom: dateFrom, DateTo: dateTo, Page: 1, PerPage: pagination.PerPage });
    setFiltersOpen(false);
  }

  function resetFilters() {
    setKeyword("");
    setFilterLocationIds([]);
    setStoreUserId("");
    setEntityType("");
    setAction("");
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    setFiltersOpen(false);
    load({ Keyword: "", LocationIds: [], StoreUserId: "", EntityType: "", Action: "", DateFrom: defaultDateFrom, DateTo: defaultDateTo, Page: 1, PerPage: pagination.PerPage });
  }

  function toggleFilterLocation(LocationId: string) {
    setFilterLocationIds((current) => (current.includes(LocationId) ? current.filter((id) => id !== LocationId) : [...current, LocationId]));
  }

  return (
    <AdminLayout sectionLabel="門市管理" title="門市操作紀錄" user={currentUser} onLogout={handleLogout}>
      <form className="mb-5 rounded-md border border-[#dfe6e1] bg-white p-3 2xl:p-4" onSubmit={handleSearch}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-extrabold text-[#203d32]"><Filter size={16} />篩選條件</div>
            <p className="mt-1 break-words text-sm font-bold leading-6 text-[#7b877f]">{filterSummary}</p>
          </div>
          <button className="inline-flex min-h-10 shrink-0 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => setFiltersOpen((current) => !current)} type="button">
            {filtersOpen ? "收合篩選" : "展開篩選"}
          </button>
        </div>
        {filtersOpen ? <div className="mt-3 grid gap-3 border-t border-[#edf0ee] pt-3 sm:grid-cols-2 2xl:grid-cols-[minmax(260px,1fr)_180px_170px_160px_150px_150px_auto] 2xl:items-end">
          <Field label="關鍵字">
            <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="資料編號、人員、門市、動作" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </Field>
          <Field label="門市人員">
            <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={storeUserId} onChange={(event) => setStoreUserId(event.target.value)}>
              <option value="">全部人員</option>
              {storeUsers.map((item) => <option key={item.StoreUserId} value={item.StoreUserId}>{item.DisplayName} ({item.Username})</option>)}
            </select>
          </Field>
          <Field label="資料類型">
            <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={entityType} onChange={(event) => setEntityType(event.target.value)}>
              <option value="">全部類型</option>
              {entityTypeOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
            </select>
          </Field>
          <Field label="動作">
            <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={action} onChange={(event) => setAction(event.target.value)}>
              <option value="">全部動作</option>
              {actionOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
            </select>
          </Field>
          <Field label="起日">
            <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </Field>
          <Field label="迄日">
            <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </Field>
          <div className="rwd-action-grid sm:col-span-2 2xl:col-span-1">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" type="submit"><Search size={18} />查詢</button>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={resetFilters} type="button"><RefreshCw size={18} />重設</button>
          </div>
        </div> : null}

        {filtersOpen ? <div className="mt-4 border-t border-[#edf0ee] pt-3">
          <div className="mb-2 grid gap-2 sm:flex sm:flex-wrap sm:items-center sm:justify-between">
            <label className="inline-flex items-center gap-2 text-sm font-bold text-[#59665f]"><Filter size={15} />門市篩選</label>
            <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-[#315746]">
              <span className="text-[#7b877f]">已選 {filterLocationIds.length} 間</span>
              <button className="hover:underline" onClick={() => setFilterLocationIds(locations.map((item) => item.LocationId))} type="button">全選</button>
              <span className="text-[#c9d2cc]">|</span>
              <button className="hover:underline" onClick={() => setFilterLocationIds([])} type="button">清除</button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 sm:flex-wrap sm:overflow-visible">
            {locations.map((location) => {
              const checked = filterLocationIds.includes(location.LocationId);
              return (
                <label className={`flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-bold ${checked ? "border-[#9ac8aa] bg-[#f1f8f3] text-[#245438]" : "border-[#d8dedb] bg-white text-[#59665f]"}`} key={location.LocationId}>
                  <input checked={checked} className="h-4 w-4 accent-[#315746]" onChange={() => toggleFilterLocation(location.LocationId)} type="checkbox" />
                  {location.Name}
                </label>
              );
            })}
          </div>
        </div> : null}
      </form>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <SummaryCard icon={<History size={18} />} label="全部紀錄" value={counts.Total || 0} />
        <SummaryCard icon={<FileJson2 size={18} />} label="新增" value={counts.Create || 0} />
        <SummaryCard icon={<Clock3 size={18} />} label="修改/狀態" value={(counts.Update || 0) + (counts.StatusUpdate || 0)} />
        <SummaryCard icon={<ShieldCheck size={18} />} label="完成/延期" value={(counts.Complete || 0) + (counts.Postpone || 0)} />
        <SummaryCard icon={<FileJson2 size={18} />} label="作廢" value={counts.Void || 0} />
      </div>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="grid min-w-0 gap-4 2xl:grid-cols-[minmax(0,1fr)_minmax(420px,.62fr)]">
        <section className="min-w-0 rounded-md border border-[#dfe6e1] bg-white">
          <div className="max-h-[68vh] overflow-auto">
            <table className="w-full min-w-[1250px] table-fixed border-separate border-spacing-0 text-left">
              <thead className="sticky top-0 z-40 bg-[#f0f3f1] text-sm text-[#59665f]">
                <tr>
                  <th className="w-[190px] whitespace-nowrap px-4 py-3 font-bold">時間</th>
                  <th className="w-[230px] whitespace-nowrap px-4 py-3 font-bold">門市 / 人員</th>
                  <th className="w-[260px] whitespace-nowrap px-4 py-3 font-bold">資料</th>
                  <th className="w-[200px] whitespace-nowrap px-4 py-3 font-bold">動作</th>
                  <th className="w-[170px] whitespace-nowrap px-4 py-3 font-bold">IP</th>
                  <th className="sticky right-0 z-50 w-[140px] whitespace-nowrap bg-[#f0f3f1] px-4 py-3 text-right font-bold shadow-[-10px_0_18px_rgba(32,61,50,.08)]">操作</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td className="px-4 py-8 text-center text-[#7b877f]" colSpan={6}>載入中...</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td className="px-4 py-8 text-center text-[#7b877f]" colSpan={6}>目前沒有符合條件的操作紀錄。</td></tr>
                ) : logs.map((item) => (
                  <tr className={`border-t border-[#edf0ee] align-top hover:bg-[#fbfcfb] ${selectedLog?.ActivityLogId === item.ActivityLogId ? "bg-[#f1f8f3]" : ""}`} key={item.ActivityLogId}>
                    <td className="whitespace-nowrap px-4 py-4 text-sm font-bold text-[#203d32]">{item.CreatedAt}</td>
                    <td className="px-4 py-4">
                      <p className="break-words font-extrabold text-[#203d32]">{item.LocationName || "-"}</p>
                      <p className="mt-1 break-all text-sm text-[#59665f]">{item.StoreUserName || item.StoreUsername || "-"}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="break-words font-extrabold text-[#203d32]">{item.EntityTypeLabel}</p>
                      <p className="mt-1 break-all text-sm text-[#59665f]">#{item.EntityId}</p>
                    </td>
                    <td className="min-w-0 px-4 py-4"><Badge tone={item.Action}>{item.ActionLabel}</Badge></td>
                    <td className="whitespace-nowrap px-4 py-4 text-sm text-[#59665f]">{item.IpAddress || "-"}</td>
                    <td className="sticky right-0 z-10 bg-white px-4 py-4 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]">
                      <button className="inline-flex h-10 min-w-[82px] items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => setSelectedLog(item)} type="button">
                        <Eye size={16} />查看
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Keyword: keyword.trim(), LocationIds: filterLocationIds, StoreUserId: storeUserId, EntityType: entityType, Action: action, DateFrom: dateFrom, DateTo: dateTo, Page: page, PerPage: pagination.PerPage })} />
        </section>

        <aside className="min-w-0 rounded-md border border-[#dfe6e1] bg-white p-4">
          {!selectedLog ? (
            <div className="flex min-h-[360px] flex-col items-center justify-center text-center text-[#59665f]">
              <History className="mb-3 text-[#9aa8a0]" size={34} />
              <p className="font-bold text-[#203d32]">選擇一筆操作紀錄</p>
              <p className="mt-2 text-sm leading-6">這裡會顯示異動前後內容、裝置資訊與稽核欄位。</p>
            </div>
          ) : (
            <div className="min-w-0 space-y-4">
              <div className="border-b border-[#edf0ee] pb-4">
                <Badge tone={selectedLog.Action}>{selectedLog.ActionLabel}</Badge>
                <h2 className="mt-3 break-words text-2xl font-extrabold text-[#203d32]">{selectedSummary}</h2>
                <p className="mt-2 text-sm font-bold text-[#7b877f]">{selectedLog.CreatedAt}</p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <InfoTile label="門市" value={selectedLog.LocationName} />
                <InfoTile label="門市人員" value={selectedLog.StoreUserName || selectedLog.StoreUsername} />
                <InfoTile label="IP" value={selectedLog.IpAddress} />
                <InfoTile label="User Agent" value={selectedLog.UserAgent} />
              </div>
              <JsonBlock title="異動前" value={selectedLog.BeforeData} />
              <JsonBlock title="異動後" value={selectedLog.AfterData} />
            </div>
          )}
        </aside>
      </div>
    </AdminLayout>
  );
}

function Field({ children, label }: { children: ReactNode; label: string }) {
  return <label className="grid min-w-0 gap-1 text-sm font-bold text-[#59665f]"><span>{label}</span>{children}</label>;
}

function SummaryCard({ icon, label, value }: { icon: ReactNode; label: string; value: number }) {
  return (
    <div className="rounded-md border border-[#dfe6e1] bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-bold text-[#7b877f]">{label}</p>
        <span className="text-[#315746]">{icon}</span>
      </div>
      <p className="mt-3 text-3xl font-extrabold text-[#203d32]">{value}</p>
    </div>
  );
}

function Badge({ children, tone }: { children: ReactNode; tone: string }) {
  return <span className={`inline-flex max-w-full rounded-full px-2.5 py-1 text-xs font-bold ${actionTone[tone] || "bg-[#f4f6f5] text-[#59665f]"}`}><span className="min-w-0 break-all leading-5">{children}</span></span>;
}

function InfoTile({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="min-w-0 rounded-md border border-[#edf0ee] bg-[#fbfcfb] px-3 py-2">
      <p className="text-xs font-bold text-[#7b877f]">{label}</p>
      <p className="mt-1 min-w-0 break-all text-sm font-extrabold leading-6 text-[#203d32]">{value || "-"}</p>
    </div>
  );
}

function JsonBlock({ title, value }: { title: string; value?: Record<string, unknown> | null }) {
  return (
    <section className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb]">
      <div className="border-b border-[#edf0ee] px-3 py-2 text-sm font-extrabold text-[#203d32]">{title}</div>
      <pre className="max-h-[320px] overflow-auto whitespace-pre-wrap break-words p-3 text-xs leading-5 text-[#34463c]">{value ? JSON.stringify(value, null, 2) : "無資料"}</pre>
    </section>
  );
}
